import React from "react";
import _ from "lodash";
import { Pagination } from "@type/common/Pagination";

interface LoaderFunc {
  (opts: object): Promise<any>;
}
interface TypeInitialParams {
  type?: string;
  page?: number;
  pageSize?: number;
  [key: string]: any;
}
interface TypeConfig {
  initialParams?: TypeInitialParams;
  loader?: LoaderFunc;
  onSearch?: (key: string, value: any) => void;
  onChangeFilter?: (
    pagination: Pagination,
    filters: object,
    sorter: object,
    columns?: any
  ) => void;
  changeFilter?: (opts?: any) => void;
  changeSort?: (opts?: any) => void;
  clearLoadingTable?: () => void;
}
interface TypeUsePaginationReturn {
  items: any[];
  page: number;
  pageSize: number;
  total: number;
  isLoading: boolean;
  errors?: any;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onLoadPage: (page: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onChangePage: (page: number) => void;
  onChangeFilter: (
    pagination: Pagination,
    filter?: object,
    sorter?: object,
    columns?: any
  ) => void;
  onSearch: (key: string, value: any) => void;
  onReload: (conf?: any) => void;
  onRefresh: () => void;
  changeFilter: ({ pagination, sorters, filters }?: any) => void;
  changeSort: ({ sorters }?: any) => void;
}
interface TypeUsePagination {
  (config?: TypeConfig): TypeUsePaginationReturn;
}
/**
 *
 * @param { initialParams: <PlainObject>, loader: <AsyncFunction> } config
 * @returns { items, page, total, isLoading, errors, hasPreviousPage, hasNextPage, onLoadPage, onPreviousPage, onNextPage, onChangePage } PaginationModel
 */
const usePagination: TypeUsePagination = (config = {}) => {
  const ref: any = React.useRef({});
  const [pageSize, $pageSize] = React.useState(
    _.get(config, "initialParams.pageSize", 10)
  );
  const type = _.get(config, "initialParams.type", "");
  const [items, $items] = React.useState([]);
  const [page, $page] = React.useState(_.get(config, "initialParams.page", 1));
  const [total, $total] = React.useState(0);
  const [isLoading, $isLoading] = React.useState(true);
  const [errors, $errors] = React.useState(null);
  _.assign(ref.current, {
    items,
    $items,
    page,
    $page,
    pageSize,
    $pageSize,
    total,
    $total,
    isLoading,
    $isLoading,
    errors,
    $errors,
  });
  const dataLoader = async (opts = {}) => {
    const { loader, clearLoadingTable } = config;

    try {
      if (!isLoading) {
        $isLoading(true);
      }

      if (_.isFunction(loader)) {
        const reload = _.get(opts, "reload", false);
        const page = _.isNumber(_.get(opts, "page"))
          ? _.get(opts, "page")
          : ref.current.page;
        const pageSize = _.isNumber(_.get(opts, "pageSize"))
          ? _.get(opts, "pageSize")
          : ref.current.pageSize;
        const payload = {
          page,
          pageSize,
        };
        const res = await loader(payload);
        const total = _.get(res, "total", 0);
        if (!_.isEqual(ref.current.total, total)) {
          ref.current.$total(total);
        }
        if (!_.isEqual(ref.current.page, page)) {
          ref.current.$page(page);
        }
        if (!_.isEqual(ref.current.pageSize, pageSize)) {
          ref.current.$pageSize(pageSize);
        }
        const newErrors = _.get(res, "errors", null);
        if (!_.isEmpty(newErrors)) {
          ref.current.$errors(newErrors);
        }
        const newItems = _.get(res, "data", []);

        if (type === "infinity" && !reload) {
          ref.current.$items([...ref.current.items, ...newItems]);
        } else {
          ref.current.$items(newItems);
        }
        $isLoading(false);

        if (_.isFunction(clearLoadingTable)) clearLoadingTable();
      }
    } catch (errors: any) {
      $errors(errors);
      $isLoading(false);
      if (_.isFunction(clearLoadingTable)) clearLoadingTable();
    }
  };
  const hasNextPage =
    ref.current.page < _.round(total / ref.current.pageSize, 2);
  const hasPreviousPage = ref.current.page > 1;
  const onLoadPage = (page: number) => {
    const pageSize = _.get(config, "pageSize") || ref.current.pageSize;
    dataLoader({ page, pageSize });
  };
  const onNextPage = () => {
    if (hasNextPage) {
      onLoadPage(ref.current.page + 1);
    }
  };
  const onPreviousPage = () => {
    if (hasPreviousPage) {
      onLoadPage(ref.current.page - 1);
    }
  };
  const onChangePage = (page: number) => {
    if (page > ref.current.page) {
      onNextPage();
    } else if (page < ref.current.page) {
      onPreviousPage();
    }
  };
  // TODO các phần liên quan TableList
  const onChangeFilter = (
    pagination: Pagination,
    filter?: object,
    sorter?: object
  ) => {
    const onChangeFilter: any = _.get(config, "onChangeFilter", () => {});
    onChangeFilter(pagination, filter, sorter);
    dataLoader({
      page: _.get(pagination, "current"),
      pageSize: _.get(pagination, "pageSize"),
    });
  };
  const onSearch = (key: string, value: any) => {
    const onSearch = _.get(config, "onSearch", () => {});
    onSearch(key, value);
  };
  const onReload = (conf?: any) => {
    ref.current.$page(1);
    const pageSize = _.get(config, "pageSize") || ref.current.pageSize;
    dataLoader({ page: 1, pageSize, reload: true, ...conf });
  };
  const onRefresh = () => {
    dataLoader();
  };
  const changeFilter = ({
    // pagination = {},
    filters = {},
    sorters = {},
  }: {
    pagination?: object;
    filters?: object;
    sorters?: object;
  }) => {
    const _pagination = {
      current: ref.current.page,
      pageSize: ref.current.pageSize,
      total: ref.current.total,
    };
    const changeFilter: any = _.get(config, "changeFilter", () => {});
    // const sorters: object = {};
    changeFilter({ pagination: _pagination, filters, sorters });
    onChangeFilter(_pagination, filters, sorters);
  };
  const changeSort = ({ sorters = {} }: { sorters?: object }) => {
    const changeSort: any = _.get(config, "changeSort", () => {});
    changeSort({ sorters });
    onReload();
  };
  return {
    items,
    page,
    pageSize,
    total,
    isLoading,
    errors,
    hasPreviousPage,
    hasNextPage,
    onLoadPage,
    onPreviousPage,
    onNextPage,
    onChangePage,
    onChangeFilter,
    onSearch,
    onReload,
    onRefresh,
    // support for useFilter
    changeFilter,
    changeSort,
  };
};

export default usePagination;
